import { useEffect, useState } from 'react';
import { organizationApi, reportsApi, exportApi, downloadJson } from '../services/api';
import { Organization } from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  FlexBox,
  Card,
  CardHeader,
  Icon,
  BusyIndicator,
  MessageStrip
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import PageIntro from '../components/PageIntro';
import { pageIntros } from '../helpers/helpTexts';

interface ReportDef {
  id: string;
  title: string;
  description: string;
  icon: string;
  article: string;
}

const reports: ReportDef[] = [
  {
    id: 'compliance',
    title: 'Compliance-Übersicht',
    description: 'Gesamtübersicht aller DSGVO-Module mit Kennzahlen und Status.',
    icon: 'badge',
    article: 'Art. 5, 24 DSGVO'
  },
  {
    id: 'vvt',
    title: 'Verarbeitungsverzeichnis',
    description: 'Vollständiges Verzeichnis aller Verarbeitungstätigkeiten gemäß Art. 30.',
    icon: 'documents',
    article: 'Art. 30 DSGVO'
  },
  {
    id: 'tom',
    title: 'TOM-Bericht',
    description: 'Übersicht aller technischen und organisatorischen Maßnahmen nach Kategorie.',
    icon: 'shield',
    article: 'Art. 32 DSGVO'
  },
  {
    id: 'service-providers',
    title: 'Dienstleister-Übersicht',
    description: 'Alle Auftragsverarbeiter und Empfänger mit AVV-Status.',
    icon: 'supplier',
    article: 'Art. 28 DSGVO'
  },
  {
    id: 'breaches',
    title: 'Vorfallbericht',
    description: 'Übersicht aller Datenschutzvorfälle mit Schweregrad und Meldestatus.',
    icon: 'alert',
    article: 'Art. 33/34 DSGVO'
  }
];

export default function ReportsPage() {
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);

  useEffect(() => { loadOrg(); }, []);

  const loadOrg = async () => {
    const res = await organizationApi.getAll();
    if (res.success && res.data && res.data.length > 0) {
      setCurrentOrg(res.data[0]);
    }
    setLoading(false);
  };

  const openHtmlReport = (reportId: string) => {
    if (!currentOrg) return;
    const url = reportsApi.getReportUrl(reportId, currentOrg.id, 'html');
    // Open in new tab with auth header via fetch + blob
    setGenerating(reportId);
    fetch(url, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
    })
      .then(res => res.text())
      .then(html => {
        const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      })
      .finally(() => setGenerating(null));
  };

  const downloadJsonReport = async (reportId: string) => {
    if (!currentOrg) return;
    setGenerating(reportId);
    const url = reportsApi.getReportUrl(reportId, currentOrg.id, 'json');
    try {
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        downloadJson(data.data, `report-${reportId}-${new Date().toISOString().slice(0, 10)}.json`);
      }
    } finally {
      setGenerating(null);
    }
  };

  const handleFullExport = async () => {
    if (!currentOrg) return;
    setGenerating('full-export');
    const res = await exportApi.exportFull(currentOrg.id);
    if (res.success && res.data) {
      downloadJson(res.data, `dsgvo-buddy-export-${new Date().toISOString().slice(0, 10)}.json`);
    }
    setGenerating(null);
  };

  if (loading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout--detail">
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>Berichte und Export</Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            DSGVO-Reports generieren und Daten exportieren
          </Text>
        </div>
      </FlexBox>

      <PageIntro {...pageIntros.reports} storageKey="reports" />

      {/* Reports */}
      <Title level="H5" style={{ marginBottom: '0.75rem' }}>DSGVO-Berichte</Title>
      <Text style={{ color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '1rem', fontSize: '0.8125rem' }}>
        Berichte werden als HTML generiert und können im Browser als PDF gedruckt werden (Strg+P / Cmd+P).
      </Text>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {reports.map(report => (
          <Card key={report.id}
            header={
              <CardHeader
                titleText={report.title}
                subtitleText={report.article}
                avatar={<Icon name={report.icon} />}
                interactive
                onClick={() => openHtmlReport(report.id)}
              />
            }
          >
            <div style={{ padding: '0 1.25rem 1.25rem' }}>
              <Text style={{ display: 'block', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
                {report.description}
              </Text>
              <FlexBox style={{ gap: '0.5rem' }}>
                <Button
                  icon="print"
                  design="Default"
                  onClick={() => openHtmlReport(report.id)}
                  disabled={generating === report.id}
                >
                  {generating === report.id ? 'Wird erstellt...' : 'HTML / Drucken'}
                </Button>
                <Button
                  icon="download"
                  design="Transparent"
                  onClick={() => downloadJsonReport(report.id)}
                  disabled={generating === report.id}
                >
                  JSON
                </Button>
              </FlexBox>
            </div>
          </Card>
        ))}
      </div>

      {/* Full Export */}
      <Title level="H5" style={{ marginBottom: '0.75rem' }}>Datenexport</Title>
      <Card
        header={
          <CardHeader
            titleText="Voll-Export"
            subtitleText="Alle Mandantendaten als JSON"
            avatar={<Icon name="download-from-cloud" />}
          />
        }
      >
        <div style={{ padding: '0 1.25rem 1.25rem' }}>
          <Text style={{ display: 'block', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
            Exportiert alle Organisationsdaten, Verarbeitungen, Dienstleister, TOM, Datenschutzhinweise und Relationen als strukturierte JSON-Datei. Geeignet für Backup oder Migration.
          </Text>
          <Button
            icon="download"
            design="Emphasized"
            onClick={handleFullExport}
            disabled={generating === 'full-export'}
          >
            {generating === 'full-export' ? 'Wird exportiert...' : 'Voll-Export starten'}
          </Button>
        </div>
      </Card>


      <MessageStrip design="Information" hideCloseButton style={{ marginTop: '1rem' }}>
        Tipp: Um einen Bericht als PDF zu speichern, öffnen Sie den HTML-Bericht und drücken Sie Strg+P (Windows) bzw. Cmd+P (Mac).
      </MessageStrip>
    </div>
  );
}
